import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { ListFilter, Search, Coins, TrendingUp, Hash, Layers, ArrowRight, Wallet } from "lucide-react";
import CardBrandIcon from "@/components/CardBrandIcon";
import CommonGrid from "@/components/pos/admin/common/grid";
import AdvanceFiltersModal, { type FilterConfig } from "@/components/pos/admin/common/advance-filters-modal";
import NoRecordFound from "@/components/pos/admin/common/no-record-found";
import AdminTransactionDetailDialog from "@/components/admin/AdminTransactionDetailDialog";
import { usePagePersistentState } from "@/hooks/pos/use-page-persisted-state";
import { useDebounce } from "@/hooks/pos/use-debounce";
import { useSortableTable } from "@/hooks/pos/use-sortable-table";
import { useDateFormatter } from "@/hooks/pos/use-date-formatter";
import { useToast } from "@/hooks/use-toast";
import { Transaction, TRANSACTION_TYPES, TransactionType } from "@/constants";
import { posApiFetch } from "@/utils/pos-api";

const fetchTransactions = async (): Promise<Transaction[]> => {
  const response = await posApiFetch("/v1/transactions");
  if (!response.ok) throw new Error("Failed to fetch transactions");
  const json = await response.json();
  // Support paginated response shape { data: { items: [...] } }
  if (json?.data?.items) return json.data.items;
  return json.data ?? json;
};

type TransactionsState = {
  typeFilter?: string;
  currentPage?: number;
  itemsPerPage?: number;
  searchQuery?: string;
  dateRange?: { from: string; to: string };
};

const defaultTransactionsState: TransactionsState = {
  typeFilter: "all",
  currentPage: 1,
  itemsPerPage: 10,
};

const normalizeDate = (value: any): Date | null => {
  if (!value) return null;
  if (value instanceof Date) return isNaN(value.getTime()) ? null : value;
  if (typeof value === "string") {
    if (!value.trim()) return null;
    const d = new Date(value);
    return isNaN(d.getTime()) ? null : d;
  }
  return null;
};

const getTypeBadgeColor = (type: string): string => {
  switch (type) {
    case "order": return "bg-blue-100 text-blue-800";
    case "membership": return "bg-purple-100 text-purple-800";
    case "entry_pass": return "bg-green-100 text-green-800";
    case "mint": return "bg-yellow-100 text-yellow-800";
    case "swap": return "bg-orange-100 text-orange-800";
    default: return "bg-gray-100 text-gray-800";
  }
};

const truncateAddress = (addr: string) => {
  if (!addr || addr.length < 10) return addr || "—";
  return `${addr.slice(0, 6)}...${addr.slice(-4)}`;
};

const typeFilterOptions = [
  { label: "All", value: "all" },
  ...Object.entries(TRANSACTION_TYPES)
    .filter(([key]) => key !== "all")
    .map(([value, label]) => ({ label, value })),
];

export default function TransactionsPage() {
  const { toast } = useToast();
  const { formatDate } = useDateFormatter();
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState<Transaction | null>(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);

  useEffect(() => { document.title = "Transactions | SECH Admin"; }, []);

  const [txState, setTxState] = usePagePersistentState<TransactionsState>("transactions", defaultTransactionsState);
  const searchQuery = txState.searchQuery;
  const currentPage = txState.currentPage;
  const itemsPerPage = txState.itemsPerPage;
  const typeFilter = txState.typeFilter;
  const dateRange = txState.dateRange;
  const debouncedSearch = useDebounce(searchQuery?.trim() ?? "", 300);

  const { sortColumn, sortDirection, handleSort } = useSortableTable<string>({
    storageKey: "transactions_table",
    defaultColumn: "timestamp",
    defaultDirection: "desc",
  });

  const [filterValues, setFilterValues] = useState<Record<string, any>>({
    typeFilter: "", dateRange: { from: "", to: "" },
  });

  useEffect(() => {
    setFilterValues((prev) => ({
      ...prev,
      typeFilter: typeFilter ?? "",
      dateRange: { from: dateRange?.from ?? "", to: dateRange?.to ?? "" },
    }));
  }, [typeFilter, dateRange]);

  const handleApplyFilters = (values: Record<string, any>) => {
    const fromDate = normalizeDate(values.dateRange?.from);
    const toDate = normalizeDate(values.dateRange?.to);
    if (fromDate && toDate && fromDate.getTime() > toDate.getTime()) {
      toast({ title: "Invalid filters", description: '"From" date cannot be after "To" date', variant: "destructive" });
      return;
    }
    setFilterValues(values);
    setTxState((prev) => ({
      ...prev,
      typeFilter: values.typeFilter || "all",
      currentPage: 1,
      dateRange: { from: values.dateRange?.from ?? "", to: values.dateRange?.to ?? "" },
    }));
  };

  const { data: transactions = [], isLoading, error } = useQuery<Transaction[]>({
    queryKey: ["/v1/transactions"],
    queryFn: fetchTransactions,
    retry: 3,
    retryDelay: 1000,
  });

  const getFilterCount = () => {
    let count = 0;
    if (txState.typeFilter && txState.typeFilter !== "all") count += 1;
    const fromDate = normalizeDate(txState.dateRange?.from);
    const toDate = normalizeDate(txState.dateRange?.to);
    if (fromDate || toDate) count += 1;
    return count;
  };

  const transactionFilters: FilterConfig[] = useMemo(() => [
    { key: "typeFilter", label: "Type", type: "select", options: typeFilterOptions, placeholder: "Select Type" },
    { key: "dateRange", label: "Date Range", type: "dateRange" },
  ], []);

  const handleClear = () => {
    setFilterValues({ typeFilter: "all", dateRange: { from: "", to: "" } });
    setTxState((prev) => ({ ...prev, typeFilter: "all", dateRange: { from: "", to: "" }, currentPage: 1 }));
  };

  const tableColumns = useMemo(() => [
    {
      label: "Date & Time", field: "timestamp", sortable: true,
      cellTemplate: (value: number) => formatDate(new Date(value).toISOString()),
    },
    {
      label: "Type", field: "type", sortable: true,
      cellTemplate: (value: TransactionType) => (
        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getTypeBadgeColor(value)}`}>
          {TRANSACTION_TYPES[value] || value}
        </span>
      ),
    },
    {
      label: "Payment Method", field: "paymentMethod",
      cellTemplate: (value: string, row: Transaction) => value === 'CARD' ? (
        row.paymentDetails?.last4 ? (
          <span className="inline-flex items-center gap-1.5 text-[13px] text-[var(--secondary-text-color)]">
            <CardBrandIcon brand={row.paymentDetails?.brand} width={28} />
            <span>••••{row.paymentDetails.last4}</span>
          </span>
        ) : (
          <span className="inline-flex items-center gap-1.5 text-[13px] text-[var(--secondary-text-color)]">
            <CardBrandIcon brand={null} width={28} />
            <span>Card</span>
          </span>
        )
      ) : (
        <span className="inline-flex items-center gap-1.5 text-[13px] text-[var(--secondary-text-color)]">
          <Wallet className="h-4 w-4 text-orange-500 shrink-0" />
          <span className="font-mono">{row.fromAddress ? truncateAddress(row.fromAddress) : "Token"}</span>
        </span>
      ),
    },
    {
      label: "From", field: "fromAddress",
      cellTemplate: (value: string) => (
        <span className="text-[13px] text-[var(--secondary-text-color)] font-mono">{truncateAddress(value)}</span>
      ),
    },
    {
      label: "To", field: "toAddress",
      cellTemplate: (value: string) => (
        <span className="text-[13px] text-[var(--secondary-text-color)] font-mono">{truncateAddress(value)}</span>
      ),
    },
    {
      label: "Amount", field: "amount", sortable: true,
      cellTemplate: (value: string, row: Transaction) => (
        <span className="text-[13px] text-[var(--secondary-text-color)] font-medium">
          {row.paymentMethod === 'CARD'
            ? `$${parseFloat(value?.toString() ?? "0").toFixed(2)}`
            : `${parseFloat(value?.toString() ?? "0").toFixed(6)} SECH`
          }
        </span>
      ),
    },
    {
      label: "Order ID", field: "orderId",
      cellTemplate: (value: string) => (
        <span className="text-[13px] text-[var(--secondary-text-color)]">{value || "—"}</span>
      ),
    },
    {
      label: "Action", field: "id",
      headerTemplate: () => <span className="text-[13px]"><span className="h-[28px] w-[28px] flex items-center justify-center">Action</span></span>,
      cellTemplate: (_value: number, row: Transaction) => (
        <span
          className="group text-[13px] hover:text-[#CC1417] text-[var(--primary-color)] flex items-center gap-2 cursor-pointer"
          onClick={() => { setSelectedTransaction(row); setIsDetailOpen(true); }}
        >
          Details
          <ArrowRight className="transform transition-transform duration-300 group-hover:translate-x-1" width={16} height={16} />
        </span>
      ),
    },
  ], []);

  const filteredTransactions = useMemo(() =>
    transactions.filter((tx) => {
      const search = debouncedSearch?.trim().toLowerCase() ?? "";
      const matchesType = typeFilter === "all" || !typeFilter || tx.type === typeFilter;
      const matchesSearch = !search
        || tx.fromAddress?.toLowerCase().includes(search)
        || tx.toAddress?.toLowerCase().includes(search)
        || tx.orderId?.toLowerCase().includes(search);
      const fromDate = normalizeDate(txState.dateRange?.from);
      const toDate = normalizeDate(txState.dateRange?.to);
      const txTime = new Date(tx.timestamp).getTime();
      const matchesDateRange = fromDate && toDate
        ? txTime >= fromDate.getTime() && txTime <= toDate.getTime()
        : true;
      return matchesType && matchesSearch && matchesDateRange;
    }), [transactions, typeFilter, debouncedSearch, txState.dateRange]
  );

  const stats = useMemo(() => {
    if (!filteredTransactions.length) return { totalVolume: "0.000000", count: 0, average: "0.000000", uniqueTypes: 0 };
    const total = filteredTransactions.reduce((sum, tx) => sum + parseFloat(tx.amount?.toString() ?? "0"), 0);
    const uniqueTypes = new Set(filteredTransactions.map((tx) => tx.type)).size;
    return {
      totalVolume: total.toFixed(6),
      count: filteredTransactions.length,
      average: (total / filteredTransactions.length).toFixed(6),
      uniqueTypes,
    };
  }, [filteredTransactions]);

  const sortedTransactions = useMemo(() => {
    return [...filteredTransactions].sort((a: any, b: any) => {
      let aVal: any, bVal: any;
      switch (sortColumn) {
        case "timestamp": aVal = a.timestamp ?? 0; bVal = b.timestamp ?? 0; break;
        case "type": aVal = a.type ?? ""; bVal = b.type ?? ""; break;
        case "amount": aVal = parseFloat(a.amount?.toString() ?? "0"); bVal = parseFloat(b.amount?.toString() ?? "0"); break;
        default: aVal = a.timestamp ?? 0; bVal = b.timestamp ?? 0;
      }
      const comparison = typeof aVal === "string" && typeof bVal === "string" ? aVal.localeCompare(bVal) : aVal - bVal;
      return sortDirection === "asc" ? comparison : -comparison;
    });
  }, [filteredTransactions, sortColumn, sortDirection]);

  const totalPages = Math.ceil(sortedTransactions.length / (itemsPerPage ?? 10));
  const startIndex = ((currentPage ?? 1) - 1) * (itemsPerPage ?? 10);
  const paginatedTransactions = sortedTransactions.slice(startIndex, startIndex + (itemsPerPage ?? 10));

  const handlePageChange = (page: number) => { setTxState((prev) => ({ ...prev, currentPage: page })); };
  const handleItemsPerPageChange = (value: string) => { setTxState((prev) => ({ ...prev, itemsPerPage: Number(value), currentPage: 1 })); };

  const pagination = { totalPages, currentPage, itemsPerPage, totalItems: sortedTransactions.length, handlePageChange, handleItemsPerPageChange };

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Card className="max-w-md"><CardContent className="p-6">
          <h2 className="text-xl font-bold text-red-600 mb-4">Error Loading Transactions</h2>
          <p className="text-gray-600 mb-4">Failed to load transactions</p>
          <Button onClick={() => window.location.reload()} className="w-full">Reload Page</Button>
        </CardContent></Card>
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto">
      <div className="page-header flex justify-between items-center h-[48px] px-[16px] border-b border-[var(--layout-border-color)]">
        <p className="page-heading text-[20px] leading-[30px] font-semibold">Transactions</p>
        <div className="search-bar-n-btns flex justify-between gap-[4px] items-center">
          <div className={`flex h-8 items-center px-1 p-2 rounded-sm ${getFilterCount() > 0 ? "bg-[var(--secondary-background-color)]" : ""}`}>
            <Button onClick={() => setIsFilterOpen(true)} variant="ghost" className={`px-2 text-[var(--primary-text-color)] flex items-center text-[13px] font-medium leading-[15px] justify-center gap-2 rounded-[4px] ${getFilterCount() > 0 ? "h-6 hover:bg-[var(--primary-background-hover-color)]" : "h-8"}`}>
              <ListFilter className="h-4 w-4" /> Filter
              {getFilterCount() > 0 && <span className="text-[13px] bg-[var(--primary-color)] h-4 w-4 flex items-center justify-center rounded-full leading-[15px] font-medium text-white">{getFilterCount()}</span>}
            </Button>
            {getFilterCount() > 0 && (
              <>
                <span className="text-[13px] text-[var(--secondary-text-color)] px-1">|</span>
                <Button type="button" onClick={handleClear} className="text-[13px] text-[var(--primary-text-color)] font-medium px-2 h-6 hover:bg-[var(--primary-background-hover-color)] bg-transparent">Clear</Button>
              </>
            )}
          </div>
          <div className="search-bar relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[#676879] w-4 h-4" />
            <input className="search h-[32px] pr-[12px] pl-[34px] text-[13px] leading-[20px] font-normal rounded-[4px] border border-[#C3C6D4] placeholder:text-[13px] placeholder:leading-[20px] placeholder:font-normal placeholder:text-[#676879] flex items-center" type="text" placeholder="Search by address or order ID" value={searchQuery ?? ""} onChange={(e) => setTxState((prev) => ({ ...prev, searchQuery: e.target.value, currentPage: 1 }))} />
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-4 gap-[12px] px-[16px] pt-[16px]">
        <div className="border border-[var(--layout-border-color)] rounded-[4px] p-4 bg-[var(--primary-background-color)]">
          <div className="flex items-center gap-2 mb-2">
            <Coins className="h-4 w-4 text-[var(--secondary-text-color)]" />
            <span className="text-[12px] font-medium text-[var(--secondary-text-color)]">Total Volume</span>
          </div>
          <p className="text-[18px] font-semibold text-[var(--primary-text-color)]">
            {stats.totalVolume} <span className="text-[12px] font-normal text-[var(--secondary-text-color)]">SECH</span>
          </p>
        </div>
        <div className="border border-[var(--layout-border-color)] rounded-[4px] p-4 bg-[var(--primary-background-color)]">
          <div className="flex items-center gap-2 mb-2">
            <Hash className="h-4 w-4 text-[var(--secondary-text-color)]" />
            <span className="text-[12px] font-medium text-[var(--secondary-text-color)]">Transaction Count</span>
          </div>
          <p className="text-[18px] font-semibold text-[var(--primary-text-color)]">{stats.count}</p>
        </div>
        <div className="border border-[var(--layout-border-color)] rounded-[4px] p-4 bg-[var(--primary-background-color)]">
          <div className="flex items-center gap-2 mb-2">
            <TrendingUp className="h-4 w-4 text-[var(--secondary-text-color)]" />
            <span className="text-[12px] font-medium text-[var(--secondary-text-color)]">Avg Amount</span>
          </div>
          <p className="text-[18px] font-semibold text-[var(--primary-text-color)]">
            {stats.average} <span className="text-[12px] font-normal text-[var(--secondary-text-color)]">SECH</span>
          </p>
        </div>
        <div className="border border-[var(--layout-border-color)] rounded-[4px] p-4 bg-[var(--primary-background-color)]">
          <div className="flex items-center gap-2 mb-2">
            <Layers className="h-4 w-4 text-[var(--secondary-text-color)]" />
            <span className="text-[12px] font-medium text-[var(--secondary-text-color)]">Transaction Types</span>
          </div>
          <p className="text-[18px] font-semibold text-[var(--primary-text-color)]">{stats.uniqueTypes}</p>
        </div>
      </div>

      <div className="p-[16px]">
        <div className="pt-0 h-[calc(100vh-220px)]">
          {!paginatedTransactions.length && !isLoading ? (
            <NoRecordFound title="Transactions" description="There are no transactions available." />
          ) : (
            <CommonGrid
              rowData={paginatedTransactions}
              isLoading={isLoading}
              columns={tableColumns}
              pagination={pagination}
              isPaginationRequired={true}
              sortColumn={sortColumn}
              sortDirection={sortDirection}
              onSort={handleSort}
            />
          )}
        </div>
      </div>
      <AdvanceFiltersModal isOpen={isFilterOpen} onClose={() => setIsFilterOpen(false)} title="Filter" filters={transactionFilters} initialValues={filterValues} onApply={handleApplyFilters} onClearAll={handleClear} />
      <AdminTransactionDetailDialog
        transaction={selectedTransaction}
        open={isDetailOpen}
        onOpenChange={(open) => { setIsDetailOpen(open); if (!open) setSelectedTransaction(null); }}
      />
    </div>
  );
}
